import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { useCollections } from '../hooks/useCollections';
import { CollectionWithRole } from '../types/collection';

interface DeleteCollectionDialogProps {
  open: boolean;
  collection: CollectionWithRole;
  onClose: () => void;
  onDelete: () => void;
}

export const DeleteCollectionDialog: React.FC<DeleteCollectionDialogProps> = ({
  open,
  collection,
  onClose,
  onDelete
}) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { deleteCollection } = useCollections();
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmationText('');
      setError(null);
      onClose();
    }
  };

  const handleDelete = async () => {
    if (confirmationText !== collection.name) {
      setError('Album name does not match');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await deleteCollection(collection.collectionId);
      handleClose();
      onDelete();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Failed to delete album');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
      <DialogTitle sx={{ position: 'relative', pr: 5 }}>
        Delete Album
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete <strong>{collection.name}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This action cannot be undone. All items will be removed from this album.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" gutterBottom>
            Type the album name to confirm:
          </Typography>
          <TextField
            fullWidth
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder={collection.name}
            error={!!error}
            helperText={error}
            disabled={isDeleting}
            autoFocus
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={confirmationText !== collection.name || isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete Album'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
